/**
 * User service for Firestore operations
 * Handles all user-related database operations with proper type safety
 */

import {
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import type { UserDocument, UserProfile } from '../types/user';
import { firestoreToUserProfile } from '../types/user';
import { userProfileSchema } from '../schemas';

// Collection references
const USERS_COLLECTION = 'users';

// This module only reads. `firestore.rules` refuses every client create,
// update and delete of `users/{uid}` (#2076, #2115): profiles are created
// and deleted server-side, and edited through PUT /v1/users/{uid}/profile.
// It reads the signed-in user's own data only: the rules grant nothing on the
// admin claim (#2139), so admin views use the API (`hooks/useAdminApi.ts`).

/**
 * Get a user by UID
 */
export async function getUser(uid: string): Promise<UserProfile | null> {
  if (!uid) {
    throw new Error('User ID is required');
  }

  const userRef = doc(db, USERS_COLLECTION, uid);

  try {
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    const userData = userSnap.data() as UserDocument;

    // Validate the data structure. The uid is the document id: test-user
    // docs created through the API have no `uid` field.
    const validatedData = userProfileSchema.parse(
      firestoreToUserProfile({ ...userData, uid: userSnap.id })
    );

    return validatedData;
  } catch (error) {
    console.error('Error getting user:', error);
    throw new Error('Failed to get user profile');
  }
}

/**
 * Get user's interactions
 */
export async function getUserInteractions(uid: string) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  const interactionsRef = collection(db, USERS_COLLECTION, uid, 'interactions');
  const interactionsQuery = query(
    interactionsRef,
    orderBy('recorded_at', 'desc')
  );

  try {
    const querySnapshot = await getDocs(interactionsQuery);
    return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error getting user interactions:', error);
    throw new Error('Failed to get user interactions');
  }
}

/**
 * Listen to user document changes
 */
export function subscribeToUser(
  uid: string,
  _callback: (user: UserProfile | null) => void
) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  // This would typically use onSnapshot, but for now we'll use a polling approach
  // In a real implementation, you'd use:
  // const userRef = doc(db, USERS_COLLECTION, uid);
  // return onSnapshot(userRef, (doc) => {
  //   if (doc.exists()) {
  //     const userData = doc.data() as UserDocument;
  //     callback(firestoreToUserProfile(userData));
  //   } else {
  //     callback(null);
  //   }
  // });

  // For now, return a cleanup function
  return () => {
    // Cleanup subscription
    // Unsubscribing from user ${uid}
  };
}

// UserBatch class removed - batch operations disabled to prevent client-side profile creation

/**
 * User service instance
 * Note: Profile creation is handled server-side by Cloud Functions.
 */
export const userService = {
  get: getUser,
  getInteractions: getUserInteractions,
  subscribe: subscribeToUser,
};
